/**
 * The zone a staff member is thinking in.
 *
 * Servers and races are scheduled by people in several countries for
 * players in more. The browser's zone is the right default and the wrong
 * thing to be stuck with: somebody in Lahore setting up a Friday-night race
 * for Europe wants to type 20:00 and mean Berlin. So the zone is a choice,
 * shown top-right, remembered per browser, and every date box on the
 * scheduling screens reads and writes in it.
 *
 * All conversion goes through Intl, so daylight saving is the browser's
 * problem and not ours.
 */
import { useEffect, useState } from 'react'

const KEY = 'br1.timezone'
const EVENT = 'br1:timezone'

export const BROWSER_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone

/** The zones on the picker. */
export const ZONES: Array<{ id: string; label: string }> = [
    // Eastern and Pacific first: they are the two the team schedules in, and
    // a picker that opens on them is one fewer thing to get wrong. The rest
    // stay for the people running EU and SEA servers.
    { id: 'America/New_York', label: 'US Eastern (EST/EDT)' },
    { id: 'America/Los_Angeles', label: 'US Pacific (PST/PDT)' },
    { id: BROWSER_ZONE, label: `This device (${BROWSER_ZONE})` },
    { id: 'UTC', label: 'UTC' },
    { id: 'America/Denver', label: 'US Mountain' },
    { id: 'America/Chicago', label: 'US Central' },
    { id: 'America/Sao_Paulo', label: 'Brazil (Sao Paulo)' },
    { id: 'Europe/London', label: 'UK' },
    { id: 'Europe/Berlin', label: 'Central Europe' },
    { id: 'Asia/Karachi', label: 'Pakistan' },
    { id: 'Asia/Manila', label: 'Philippines' },
    { id: 'Asia/Singapore', label: 'Singapore' },
    { id: 'Asia/Tokyo', label: 'Japan' },
    { id: 'Australia/Sydney', label: 'Sydney' },
].filter((z, i, all) => all.findIndex((x) => x.id === z.id) === i)

export const getZone = (): string => {
    try {
        return localStorage.getItem(KEY) || 'America/New_York'
    } catch {
        return BROWSER_ZONE
    }
}

export const setZone = (zone: string) => {
    try {
        localStorage.setItem(KEY, zone)
    } catch {
        /* the page still uses it for this visit */
    }
    window.dispatchEvent(new CustomEvent(EVENT, { detail: zone }))
}

/** The current zone, live: every box on the page changes together. */
export const useZone = (): [string, (z: string) => void] => {
    const [zone, set] = useState(getZone)
    useEffect(() => {
        const onChange = (e: Event) => set((e as CustomEvent).detail ?? getZone())
        window.addEventListener(EVENT, onChange)
        return () => window.removeEventListener(EVENT, onChange)
    }, [])
    return [zone, setZone]
}

const pad = (n: number) => String(n).padStart(2, '0')

/** The wall-clock parts of an instant, in a zone. */
const partsIn = (date: Date, zone: string) => {
    const f = new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    })
    const get = (type: string) => Number(f.formatToParts(date).find((p) => p.type === type)?.value ?? 0)
    return { y: get('year'), m: get('month'), d: get('day'), h: get('hour') % 24, min: get('minute'), s: get('second') }
}

/** A zone's offset from UTC at an instant, in minutes. */
const offsetAt = (date: Date, zone: string): number => {
    const p = partsIn(date, zone)
    const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.min, p.s)
    return (asUtc - date.getTime()) / 60_000
}

/** "2026-09-15" + "20:00" in a zone -> a UTC ISO string. Two passes, so the
 *  offset used is the one in force at the time being typed, not now. */
export const zonedToUtc = (dateStr: string, timeStr: string, zone: string): string | null => {
    if (!dateStr) return null
    const [y, m, d] = dateStr.split('-').map(Number)
    const [h, min] = (timeStr || '00:00').split(':').map(Number)
    let guess = Date.UTC(y, m - 1, d, h, min)
    for (let i = 0; i < 2; i += 1) guess = Date.UTC(y, m - 1, d, h, min) - offsetAt(new Date(guess), zone) * 60_000
    return new Date(guess).toISOString()
}

/** A UTC ISO string -> the date and time boxes, in a zone. */
export const utcToZoned = (iso: string | null | undefined, zone: string): { date: string; time: string } => {
    if (!iso) return { date: '', time: '' }
    const p = partsIn(new Date(iso), zone)
    return { date: `${p.y}-${pad(p.m)}-${pad(p.d)}`, time: `${pad(p.h)}:${pad(p.min)}` }
}

/** Readable, in a zone: "Fri 12 Sep, 20:00". */
export const showInZone = (iso: string | null | undefined, zone: string): string =>
    iso
        ? new Date(iso).toLocaleString(undefined, {
              timeZone: zone,
              weekday: 'short',
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
          })
        : ''

/** Today, and now, in a zone -- for the Today / Now shortcuts. */
export const todayIn = (zone: string) => utcToZoned(new Date().toISOString(), zone)

/** An instant as ISO carrying the chosen zone's own offset, e.g.
 *  "2026-09-17T12:30:00-04:00" rather than a Z string.
 *
 *  The game parses these as a DateTimeOffset, so any true offset works. This
 *  writes the operator's, because a playtest typed as 8pm Eastern should read
 *  back as 20:00-04:00 in the stored JSON rather than as midnight UTC: the
 *  file is read by people too. The offset is taken at the instant itself, so a
 *  window in August and one in December each get the one in force then.
 */
export const toOffsetIso = (iso: string | null | undefined, zone: string): string | null => {
    if (!iso) return null
    const at = new Date(iso)
    const { date, time } = utcToZoned(iso, zone)
    // Minutes east of UTC: negative through the Americas.
    const east = offsetAt(at, zone)
    const sign = east < 0 ? '-' : '+'
    const abs = Math.abs(east)
    const hh = String(Math.floor(abs / 60)).padStart(2, '0')
    const mm = String(abs % 60).padStart(2, '0')
    return `${date}T${time}:00${sign}${hh}:${mm}`
}
